import {
  BoundCheckboxField,
  BoundMultiSelectField,
  BoundTextField,
  Button,
  Css,
  DnDGrid,
  DnDGridItemHandle,
  FormLines,
  IconButton,
  Palette,
  SubmitButton,
  Tooltip,
  useComputed,
  useDnDGridItem,
  useSnackbar,
  useSuperDrawer,
  useTestIds,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import uniqueId from "lodash/uniqueId";
import { runInAction } from "mobx";
import { Observer } from "mobx-react";
import { useRef } from "react";
import { MADTypeBoundSelectField } from "src/components/autoPopulateSelects/MADTypeBoundSelectField";
import { UnitsOfMeasureBoundSelectField } from "src/components/autoPopulateSelects/UnitsOfMeasureBoundSelectField";
import { IncrementalCollectionOp, MaterialAttributeDimensionType, SaveItemsInput } from "src/generated/graphqlTypes";
import { useReaction } from "src/hooks";
import {
  AdminItemsDrawerDocument,
  AdminItemsDrawerQuery,
  useAdminItemsDrawerQuery,
  useSaveItemDimensionsMutation,
} from "src/routes/admin/items/AdminItemsDrawer.generated";

type MaterialCatalogEditorTabProps = { itemId: string };
export function MaterialCatalogEditorTab({ itemId }: MaterialCatalogEditorTabProps) {
  const { data } = useAdminItemsDrawerQuery({ variables: { itemId } });
  const [saveItemDimensions, { loading }] = useSaveItemDimensionsMutation({
    refetchQueries: [AdminItemsDrawerDocument],
    awaitRefetchQueries: true,
  });
  const { closeDrawer } = useSuperDrawer();

  const formState = useFormState({
    config: formConfig,
    readOnly: loading,
    addRules(state) {
      // Ensure `materialNameFormat` isn't used any removed dimensions
      state.materialNameFormat.rules.push(({ value }) => {
        if (!value) return;
        if (
          formState.mads.rows
            .filter((mad) => mad.op.value === IncrementalCollectionOp.Delete)
            .some((mad) => mad.name.value && value.toLowerCase().includes(mad.name.value.toLowerCase()))
        ) {
          // Rules shouldn't have a side effect, but make sure the user can see this error message (untouched fields do
          // not show their errors). Use `runInAction` to avoid the mobx warning about computed causing side effects.
          runInAction(() => {
            state.materialNameFormat.touched = true;
          });
          return "Contains deleted attributes";
        }
      });
    },
    init: { input: data, map: mapToForm },
  });

  async function onSave() {
    await saveItemDimensions({ variables: { input: mapToInput(formState, itemId) } });
  }

  const mads = useComputed(
    () => formState.mads.rows.filter((mad) => mad.op.value !== IncrementalCollectionOp.Delete),
    [formState.mads.rows],
  );

  return (
    <div css={Css.w100.$}>
      <Observer>
        {() => (
          <div css={Css.df.fdc.gap2.$}>
            <div css={Css.df.$}>
              <div css={Css.fg1.df.fdc.gap2.$}>
                <BoundTextField field={formState.materialCodePrefix} />
                <Tooltip title="To ensure a specific delimiter is always kept, wrap it in {delimiter!}">
                  <BoundTextField
                    field={formState.materialNameFormat}
                    placeholder="{width}x{height} {-!} {length} {name}"
                  />
                </Tooltip>

                <div css={Css.df.gap2.$}>
                  <BoundCheckboxField
                    label="Combine in Assembly"
                    field={formState.isMaterialModifier}
                    disabled={!data?.items.first?.canBecomeMaterialModifier.allowed}
                    helperText={
                      data?.items.first?.canBecomeMaterialModifier.disabledReasons.map((dr) => dr.message).join("; ") ||
                      "Marks that this item modifies other lines in an assembly, such as Wood Type (e.g. Walnut), rather than constituting its own line-item. Modifiers may not be bid against or create ProjectItems."
                    }
                  />
                  {formState.isMaterialModifier.value && (
                    <BoundMultiSelectField
                      label="Parent Modifiers"
                      field={formState.parentModifierItemIds}
                      options={data?.allItems ?? []}
                      getOptionLabel={(o) => o.name}
                      getOptionValue={(o) => o.id}
                    />
                  )}
                </div>
              </div>
              <div css={Css.mla.df.aic.gap2.$}>
                <Button label="Cancel" variant="quaternary" onClick={closeDrawer} />
                <SubmitButton form={formState} label="Save" onClick={onSave} />
              </div>
            </div>
            <div css={Css.wPx(150).$}>
              <Button icon="plus" onClick={() => addDimension(formState, itemId)} label="New Attribute" />
            </div>
            <AttributeGrid onReorder={(data) => reorderAttributes(data, formState)}>
              {mads.nonEmpty ? (
                mads.map((row) => (
                  <MaterialAttributeDimensionView formState={formState} itemId={itemId} row={row} key={row.id.value} />
                ))
              ) : (
                <div>No Attributes</div>
              )}
            </AttributeGrid>
          </div>
        )}
      </Observer>
    </div>
  );
}

type AttributeGridProps = { children: React.ReactNode; onReorder: (ids: string[]) => void };

export function AttributeGrid({ children, onReorder }: AttributeGridProps) {
  const tid = useTestIds({});
  return (
    <div css={Css.maxwPx(600).$}>
      <DnDGrid
        onReorder={onReorder}
        gridStyles={Css.dg.gtc("1fr").gapPx(20).$}
        activeItemStyles={
          Css.boxShadow(
            `0 0 0 4px ${Palette.Blue700}, 0px 20px 25px -5px rgba(0,0,0,0.1), 0px 10px 10px -5px rgba(0,0,0,0.04)`,
          ).$
        }
        {...tid.materialAttributes}
      >
        {children}
      </DnDGrid>
    </div>
  );
}

type MaterialAttributeProps = {
  row: ObjectState<FormMaterialAttributeDimension>;
  formState: ObjectState<FormValue>;
  itemId: string;
};

function MaterialAttributeDimensionView(props: MaterialAttributeProps) {
  const { formState, row } = props;
  const itemRef = useRef(null);
  const { triggerNotice } = useSnackbar();
  const { dragItemProps, dragHandleProps } = useDnDGridItem({ id: row.id.value, itemRef });

  function addValue(row: ObjectState<FormMaterialAttributeDimension>) {
    row?.materialAttributeValues.add({
      id: undefined,
      code: undefined,
      textValue: undefined,
      op: IncrementalCollectionOp.Include,
    });
  }

  /**
   * If the MAD has an ID, do not allow the user to remove the attribute.
   * Otherwise, we assume the entity is new and can safely be removed from the formState.
   * TODO: we could add back removal functionality (see https://github.com/homebound-team/internal-frontend/pull/6434/files)
   * once we define what should happen to MaterialVariants when a MAD is removed.
   */
  async function removeDimension(row: ObjectState<FormMaterialAttributeDimension>) {
    if (row.id.value) {
      triggerNotice({
        message:
          "Removing existing dimensions is disabled because it can cause associated materials to be soft-deleted.",
        icon: "warning",
      });
    } else {
      formState.mads.remove(row.value);
    }
  }

  function removeValue(row: ObjectState<FormMaterialAttributeDimension>, mav: ObjectState<FormMaterialAttributeValue>) {
    mav.id.value ? mav.set({ op: IncrementalCollectionOp.Delete }) : row?.materialAttributeValues.remove(mav.value);
  }

  useReaction(
    () => [row.type.value, row.unitOfMeasureId.value],
    ([type, uom]) => {
      if (type !== MaterialAttributeDimensionType.Number && uom) {
        row.unitOfMeasureId.set(undefined);
      }
    },
    [row.type.value, row.unitOfMeasureId.value],
  );

  return (
    <Observer>
      {() => (
        <section css={Css.bgGray100.py2.px1.br12.df.gap1.$} {...dragItemProps} ref={itemRef}>
          <DnDGridItemHandle icon="drag" dragHandleProps={dragHandleProps} />
          <FormLines>
            <div css={Css.df.gap1.$}>
              <div css={Css.wPx(300).$}>
                <BoundTextField label="Attribute Name" field={row.name} />
              </div>
              <div>
                <MADTypeBoundSelectField label="Type" field={row.type} />
              </div>
              {row.type.value === MaterialAttributeDimensionType.Number && (
                <div>
                  <UnitsOfMeasureBoundSelectField field={row.unitOfMeasureId} />
                </div>
              )}
              <div css={Css.ptPx(30).ml2.$}>
                <IconButton icon="x" onClick={() => removeDimension(row)} tooltip="Remove Attribute" />
              </div>
            </div>
            <div css={Css.df.gap2.$}>
              <BoundCheckboxField label="Use in Takeoff" field={row.useInTakeoff} />
              <BoundCheckboxField label="Show in Finish Schedule" field={row.showInFinishSchedule} />
              <BoundCheckboxField label="Use in Visual Mode" field={row.useInVisualModePdf} />
            </div>
            {row.type.value !== MaterialAttributeDimensionType.Number && (
              <div>
                {/* Material Attribute Values */}
                <div>
                  {row.materialAttributeValues.rows.filter((val) => val.op.value !== IncrementalCollectionOp.Delete)
                    .nonEmpty && (
                    <div>
                      <div css={Css.mb2.smSb.$}>Attribute Values</div>
                      {row.materialAttributeValues.rows
                        .filter((val) => val.op.value !== IncrementalCollectionOp.Delete)
                        .map((attrVal, index) => {
                          return (
                            <div key={index} css={Css.df.mb1.$}>
                              <div css={Css.df.gap1.w100.$}>
                                <BoundTextField
                                  // New fields are set to default as undefined. We utilize this to autofocus the field.
                                  autoFocus={attrVal.textValue.value === undefined}
                                  key={attrVal.id.value}
                                  label="Attribute Value"
                                  field={attrVal.textValue}
                                  labelStyle="hidden"
                                />
                                <BoundTextField label="Code" field={attrVal.code} labelStyle="inline" />
                              </div>
                              <div css={Css.ptPx(8).ml2.$}>
                                <IconButton icon="x" onClick={() => removeValue(row, attrVal)} tooltip="Remove Value" />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
                {row.type.value === MaterialAttributeDimensionType.Select && (
                  <div css={Css.mb2.$}>
                    <Button variant="secondary" icon="plus" onClick={() => addValue(row)} label="Add value" />
                  </div>
                )}
              </div>
            )}
          </FormLines>
        </section>
      )}
    </Observer>
  );
}

function nextSortOrder(formState: ObjectState<FormValue>) {
  if (formState.mads.rows.isEmpty) return 0;
  return Math.max(...formState.mads.rows.map((row) => row.sortOrder.value).compact()) + 1;
}

function reorderAttributes(sortedIds: string[], formState: ObjectState<FormValue>) {
  sortedIds.forEach((id, index) => {
    const match = formState.mads.rows.find((row) => row.id.value === id);
    match?.set({ sortOrder: index });
  });
}

function addDimension(formState: ObjectState<FormValue>, itemId: string) {
  formState.mads.add({
    id: uniqueId("mavId"), // Temp ID to use for reordering until the actual ID is hydrated from the BE
    name: undefined,
    type: MaterialAttributeDimensionType.Select,
    itemId: itemId,
    unitOfMeasureId: null,
    useInTakeoff: true,
    showInFinishSchedule: true,
    useInVisualModePdf: false,
    sortOrder: nextSortOrder(formState),
    materialAttributeValues: [],
    op: IncrementalCollectionOp.Include,
  });
}

export type FormMaterialAttributeDimension = {
  id: string;
  itemId: string | undefined | null;
  name: string | undefined | null;
  type: MaterialAttributeDimensionType | undefined | null;
  useInTakeoff: boolean | undefined | null;
  showInFinishSchedule: boolean | undefined | null;
  useInVisualModePdf: boolean | undefined | null;
  unitOfMeasureId: string | undefined | null;
  sortOrder: number;
  materialAttributeValues: FormMaterialAttributeValue[];
  op: IncrementalCollectionOp;
};

type FormMaterialAttributeValue = {
  id: string | undefined | null;
  code?: string | undefined | null;
  textValue: string | undefined | null;
  op: IncrementalCollectionOp;
};

type FormValue = {
  materialCodePrefix: string | undefined | null;
  materialNameFormat: string | undefined | null;
  isMaterialModifier: boolean | undefined | null;
  parentModifierItemIds: string[] | undefined | null;
  mads: FormMaterialAttributeDimension[];
};

const formConfig: ObjectConfig<FormValue> = {
  materialCodePrefix: { type: "value" },
  materialNameFormat: { type: "value" },
  isMaterialModifier: { type: "value" },
  parentModifierItemIds: { type: "value" },
  mads: {
    type: "list",
    config: {
      id: { type: "value" },
      itemId: { type: "value" },
      type: { type: "value" },
      name: { type: "value", rules: [required] },
      useInTakeoff: { type: "value" },
      showInFinishSchedule: { type: "value" },
      useInVisualModePdf: { type: "value" },
      unitOfMeasureId: { type: "value" },
      sortOrder: { type: "value" },
      op: { type: "value" },
      materialAttributeValues: {
        type: "list",
        config: { id: { type: "value" }, code: { type: "value" }, textValue: { type: "value" }, op: { type: "value" } },
      },
    },
  },
};

function mapToForm(data: AdminItemsDrawerQuery | null | undefined) {
  const [item] = data?.items ?? [];
  return {
    materialCodePrefix: item?.materialCodePrefix,
    materialNameFormat: item?.materialNameFormat,
    isMaterialModifier: item?.isMaterialModifier,
    parentModifierItemIds: item?.parentModifierItems.map((i) => i.id) ?? [],
    mads: item?.materialAttributeDimensions.map((mad) => ({
      ...mad,
      type: mad.type.code,
      itemId: item?.id,
      useInTakeoff: mad.useInTakeoff,
      showInFinishSchedule: mad.showInFinishSchedule,
      useInVisualModePdf: mad.useInVisualModePdf,
      unitOfMeasureId: mad.unitOfMeasure?.id,
      op: IncrementalCollectionOp.Include,
      materialAttributeValues: mad.values?.map((mav) => ({ ...mav, op: IncrementalCollectionOp.Include })) ?? [],
    })),
  } satisfies FormValue;
}

function mapToInput(formState: ObjectState<FormValue>, itemId: string) {
  return {
    items: [
      {
        id: itemId,
        materialCodePrefix: formState.changedValue.materialCodePrefix,
        materialNameFormat: formState.changedValue.materialNameFormat,
        isMaterialModifier: formState.changedValue.isMaterialModifier,
        parentModifierItemIds: formState.changedValue.parentModifierItemIds,
        materialAttributeDimensions: formState.value.mads.map((mad) => ({
          ...mad,
          id: mad.id.includes("mavId") ? undefined : mad.id,
        })),
      },
    ],
  } satisfies SaveItemsInput;
}
